import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AngularEditorConfig} from '@kolkov/angular-editor';
import {ImageCroppedEvent} from 'ngx-image-cropper';
import {DashboardService} from '../dashboard.service';
import {ToastrService} from 'ngx-toastr';
import {Router} from '@angular/router';
import {EDITOR_CONFIG} from '../../constants';
import {MatStepper} from '@angular/material/stepper';
import {UtilsService} from '../../core/services/utils/utils.service';
import {Category} from '../../core/models/category';
import {Entity} from '../../core/models/entities/entity';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import { HttpClient, HttpEventType } from '@angular/common/http';
import {ApiService} from '../../core/http/api.service';
import {createCroppedImageFile, getUploadProgress, isImageFile} from '../../core/services/utils/image-upload.utils';

export interface InfoParams {
  lookupEntities?: Entity[];
  createdAt: number;
  updatedAt: number;
  id: string;
  fieldName: string;
  fieldType: string;
  isRequired: boolean;
  category: string;
  lookUpRecordId: any;
}

@Component({
    selector: 'app-new-entity',
    templateUrl: './new-entity.component.html',
    styleUrls: ['./new-entity.component.css'],
    standalone: false
})

export class NewEntityComponent implements OnInit {

  // IMAGE UPLOAD
  modalRef: BsModalRef;
  croppedFile: any;
  aspectRatio = 1;
  uploading = false;
  progress = 0;
  entities: Array<Entity>;

  constructor(
    private dashApi: DashboardService,
    private modalService: BsModalService,
    private toastr: ToastrService,
    private router: Router,
    private utils: UtilsService,
    private httpClient: HttpClient,
    private api: ApiService,
  ) {
  }

  @ViewChild('stepper') stepper: MatStepper;

  categoryList: Category[] = [];
  isError = false;
  errMessage = '';
  informativeParameters: InfoParams[] = [];
  subjectiveParameters: any[] = [];
  editorConfig: AngularEditorConfig = EDITOR_CONFIG;

  imageChangedEvent: any = '';
  croppedImage: any = '';
  activeTab = 0;

  payload = {
    category: '',
    firstName: '',
    lastName: '',
    date: '',
    label: '',
    image: '',
    in_person: true,
    infoData: [],
    subjectiveData: [],
    allowedUserActions: {
      bhakt: true,
      unbhakt: true,
      rating: true,
      story: true,
      hypocrisy: true
    },
    related_entity: [],
  };
  loading = false;

  ngOnInit(): void {
    this.getCategories();
    this.getAllEntities();
  }

  getCategories(): void {
    this.dashApi.getAllCategories('').subscribe(res => {
      const categories = Array.isArray(res?.categories)
        ? res.categories
        : (Array.isArray(res) ? res : []);

      this.categoryList = categories;

      if (!categories.length) {
        this.payload.category = '';
        return;
      }

      this.payload.category = categories[0].id;
      this.onOptionsSelected(categories[0].id);
    }, err => this.utils.resErr(err));
  }

  getAllEntities(): void {
    this.api.getAllEntities({category: null, sort: 'DESC'}).subscribe(res => {
      this.entities = res?.response?.entities || [];
    });
  }

  onOptionsSelected(value: string): void {
    this.dashApi.getInfoParamsByCategory(value).subscribe((data) => {
      this.informativeParameters = data;
      this.payload.infoData = [];
      this.informativeParameters.forEach(info => this.payload.infoData.push({
        field: info.id,
        value: '',
        fieldType: info.fieldType,
        fieldName: info.fieldName,
        metadata: null
      }));
      this.getEntities();
    });
    this.dashApi.getSubParamsByCategory(value).subscribe((data) => {
      this.subjectiveParameters = data;
      this.payload.subjectiveData = [];
      this.subjectiveParameters.forEach(sub => this.payload.subjectiveData.push(''));
    });
  }

  createEntity(): void {

    if (!!this.payload.image) {
      if (this.checkSubParams()) {
        this.dashApi.createEntity(this.payload).subscribe(res2 => {
          this.isError = false;
          this.utils.goBack();
        }, (err) => {
          this.utils.resErr(err);
          this.isError = true;
          this.errMessage = err.error.message;
          this.loading = false;
        });
      } else {
        this.loading = false;
      }
    } else {
      this.utils.toastError('Upload profile image');
      this.loading = false;
    }

  }

  // IMAGE CROPPER
  fileChangeEvent(event: any, template: TemplateRef<any>): void {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }
    if (!isImageFile(file)) {
      this.utils.toastError('Please select an image file only');
      event.target.value = '';
      return;
    }
    this.modalRef = this.modalService.show(template);
    this.aspectRatio = 1;
    this.croppedFile = null;
    this.imageChangedEvent = event;
  }

  uploadImage(): any {
    if (!this.croppedFile) {
      this.utils.toastError('Please crop the selected image');
      return;
    }
    this.uploading = true;
    const formData = new FormData();
    formData.append('file', this.croppedFile, this.croppedFile?.name || `upload-${Date.now()}.png`);
    const upload = this.httpClient.post<any>('file-upload', formData, {
      reportProgress: true,
      observe: 'events'
    });
    upload.subscribe(
      (res: any) => {
        if (res.type === HttpEventType.UploadProgress) {
          this.progress = getUploadProgress(res.loaded, res.total);
        } else if (res.type === HttpEventType.Response) {
          this.uploading = false;
          this.payload.image = res.body.response.url;
          this.progress = 0;
          this.closeCropperModal();
        }
      },
      (err) => {
        this.uploading = false;
        this.utils.resErr(err);
      }
    );
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedFile = createCroppedImageFile(event);
  }

  loadImageFailed(): void {
    this.utils.toastError('Image Load failed!');
  }

  closeCropperModal(): void {
    if (this.uploading) {
      this.utils.toastError('Please wait for upload to complete');
      return;
    }
    if (this.modalRef) {
      this.modalRef.hide();
    }
    if (this.imageChangedEvent?.target) {
      this.imageChangedEvent.target.value = '';
    }
    this.imageChangedEvent = null;
    this.croppedFile = null;
    this.uploading = false;
    this.progress = 0;
  }

  next(): any {
    if (!this.payload.image) {
      this.utils.toastError('Upload profile image to continue');
      return false;
    }
    this.stepper.next();
  }

  handleClickUpload(): void {
    const ele = document.getElementById('dp_entity');
    if (!!ele) {
      ele.click();
    }
  }

  checkSubParams(): boolean {
    let temp = true;
    if (this.subjectiveParameters.length) {
      this.subjectiveParameters.forEach((s, i) => {
        if (this.payload.subjectiveData[i] === '') {
          this.utils.toastError('Please fill all the subjective Params');
          temp = false;
          return temp;
        }
      });
      return temp;
    } else {
      return temp;
    }
  }

  findCategory(e): void {
    const tempCat = this.categoryList.find(c => c.id === e);
    this.payload.allowedUserActions = tempCat?.allowedUserActions ? {...tempCat.allowedUserActions} : {
      bhakt: true,
      unbhakt: true,
      rating: true,
      story: true,
      hypocrisy: true
    };
  }

  getEntities(): void {
    this.informativeParameters.forEach(info => {
      if (info.fieldType === 'lookUp'){
        this.dashApi.searchEntities({category: info.lookUpRecordId}).subscribe(res => {
          info.lookupEntities = res.response.entities;
        }, err => this.utils.resErr(err));
      }
    });
  }

  filterEntities(categoryId): any {
    return this.entities.filter(val => val.category?.id === categoryId);
  }

}
